import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  Minus,
  Plus,
  Trash2,
  ShieldCheck,
  RotateCcw,
  MapPin,
  Check,
  Home,
  Building,
  User,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useAddress, type Address } from '@/context/AddressContext';
import { MetroProgress } from '@/components/checkout/MetroProgress';
import { calculatePriceSummary, type ShippingConfig } from '@/utils/priceUtils';
import { authFetch } from '@/services/api';

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, total, refreshCart } = useCart();
  const { user, setShowSignInModal } = useAuth();
  const { defaultAddress, addresses, setDefaultAddress } = useAddress();

  // Address selection states
  const [selectedAddress, setSelectedAddressState] = useState<Address | null>(null);
  const [isAddressExpanded, setIsAddressExpanded] = useState(false);

  // Address helper functions
  const handleSelectAddress = async (address: Address) => {
    try {
      await setDefaultAddress(address.addressId);
      setSelectedAddressState(address);
      setIsAddressExpanded(false);
    } catch (error) {
      console.error('Failed to update address:', error);
    }
  };

  const getAddressTypeIcon = (type: string) => {
    switch (type) {
      case 'HOME': return Home;
      case 'WORK': return Building;
      case 'OTHER': default: return User;
    }
  };

  const getAddressTypeColor = (type: string) => {
    switch (type) {
      case 'HOME': return 'text-green-600';
      case 'WORK': return 'text-blue-600';
      case 'OTHER': default: return 'text-gray-600';
    }
  };

  // Update selected address when default changes
  useEffect(() => {
    setSelectedAddressState(defaultAddress);
  }, [defaultAddress]);

  // Server-driven shipping config (like mobile)
  const [shippingConfig, setShippingConfig] = useState<ShippingConfig>({
    enabled: false,
    freeThreshold: 0,
    flatCharge: 0,
    gstRate: 0,
  });

  // Fetch shipping config from server
  useEffect(() => {
    let mounted = true;
    
    const fetchShippingConfig = async () => {
      try {
        const resp = await authFetch('/api/config/shipping');
        if (!resp.ok) return;
        
        const cfg = await resp.json();
        if (!mounted) return;
        
        setShippingConfig({
          enabled: !!cfg.enabled,
          freeThreshold: Number(cfg.freeThreshold || 0),
          flatCharge: Number(cfg.flatCharge || 0),
          gstRate: Number(cfg.gstRate || 0),
        });
      } catch (e) {
        console.warn('Failed to fetch shipping config', e);
      }
    };
    
    fetchShippingConfig();
    return () => { mounted = false; };
  }, []);

  // Calculate price summary using server config
  const priceSummary = useMemo(() => {
    return calculatePriceSummary({
      items: cart,
      shippingConfig,
    });
  }, [cart, shippingConfig]);

  // Sync cart on mount/focus
  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const handleProceedToReview = () => {
    if (!user.isAuthenticated) {
      setShowSignInModal(true);
      return;
    }
    navigate('/review-order');
  };

  const handleQuantityChange = async (variantId: number, newQty: number) => {
    if (newQty < 1) {
      await removeFromCart(undefined, variantId);
    } else {
      await updateQuantity(variantId, newQty);
    }
  };

  const totalQty = useMemo(
    () => cart.reduce((sum, item) => sum + item.quantity, 0),
    [cart]
  );

  // Empty Cart State
  if (cart.length === 0) {
    return (
      <Layout>
        <MetroProgress steps={['Cart', 'Review', 'Payment']} currentStep={0} />

        <div className="container mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center">
            <svg className="h-10 w-10 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-4">Your cart is empty</h1>
          <p className="text-muted-foreground mb-8">Add some products to get started!</p>
          <Button variant="cta" onClick={() => navigate('/')}>
            Continue Shopping
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <MetroProgress steps={['Cart', 'Review', 'Payment']} currentStep={0} />

      <div className="container mx-auto px-4 py-4 md:py-6">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 md:mb-6 transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
          <span>Back</span>
        </button>

        <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="space-y-4">
              {cart.map((item) => {
                const image = item.images?.[0]?.imageUrl || '/placeholder-product.png';
                const hasDiscount = item.originalPrice && item.originalPrice > item.salePrice;
                const discountPercent = hasDiscount
                  ? Math.round(((item.originalPrice! - item.salePrice) / item.originalPrice!) * 100)
                  : 0;

                return (
                  <motion.div
                    key={item.variantId}
                    layout
                    className="bg-card rounded-xl p-4 border shadow-sm"
                  >
                    <div className="flex gap-4">
                      {/* Product Image */}
                      <div className="flex-shrink-0">
                        <img
                          src={image}
                          alt={item.productName}
                          className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-lg border"
                        />
                      </div>

                      {/* Product Details */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm md:text-base mb-1 line-clamp-2">
                          {item.productName}
                        </h3>

                        <div className="text-xs md:text-sm text-muted-foreground mb-2">
                          <span>Size: {item.size}</span>
                          <span className="mx-2">•</span>
                          <span>Color: {item.color}</span>
                        </div>

                        {/* Price */}
                        <div className="flex items-center gap-2 mb-3">
                          <span className="font-semibold text-sm md:text-base">₹{item.salePrice}</span>
                          {hasDiscount && (
                            <>
                              <span className="text-xs text-muted-foreground line-through">
                                ₹{item.originalPrice}
                              </span>
                              <span className="text-xs text-green-600 font-medium">
                                {discountPercent}% OFF
                              </span>
                            </>
                          )}
                        </div>

                        {/* Quantity and Remove */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleQuantityChange(item.variantId, item.quantity - 1)}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>

                            <span className="w-8 text-center text-sm font-medium">
                              {item.quantity}
                            </span>

                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleQuantityChange(item.variantId, item.quantity + 1)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>

                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => removeFromCart(undefined, item.variantId)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Line Total */}
                        <div className="mt-2 text-right">
                          <span className="font-semibold">₹{item.salePrice * item.quantity}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-card rounded-xl p-4 md:p-6 shadow-card sticky top-24">
              <h2 className="font-semibold text-base md:text-lg mb-4">Order Summary</h2>

              {/* Delivery Address Section */}
              {user.isAuthenticated && (
                <div className="mb-4 pb-4 border-b">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Delivery Address
                  </h3>
                  {selectedAddress || defaultAddress ? (
                    <div className="space-y-3">
                      <div className="p-3 border rounded-lg bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{(selectedAddress || defaultAddress)?.fullName}</p>
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {(selectedAddress || defaultAddress)?.addressLine1}
                              {(selectedAddress || defaultAddress)?.addressLine2 && `, ${(selectedAddress || defaultAddress)?.addressLine2}`}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {(selectedAddress || defaultAddress)?.city}, {(selectedAddress || defaultAddress)?.state} - {(selectedAddress || defaultAddress)?.postalCode}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsAddressExpanded(!isAddressExpanded)}
                            className="text-xs"
                          >
                            Change
                          </Button>
                        </div>
                      </div>

                      {/* Address Selection Modal */}
                      <AnimatePresence>
                        {isAddressExpanded && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="p-3 bg-secondary/50 rounded-lg border"
                          >
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-sm">Select Address</h4>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setIsAddressExpanded(false)}
                                className="h-6 px-2 text-xs"
                              >
                                Cancel
                              </Button>
                            </div>

                            <div className="space-y-2 max-h-40 overflow-y-auto">
                              {addresses.map((address) => {
                                const AddressIcon = getAddressTypeIcon(address.addressType);
                                const iconColor = getAddressTypeColor(address.addressType);
                                const currentAddress = selectedAddress || defaultAddress;

                                return (
                                  <div
                                    key={address.addressId}
                                    className={`p-2 rounded border cursor-pointer transition-all text-xs ${
                                      currentAddress?.addressId === address.addressId
                                        ? 'border-primary bg-primary/5'
                                        : 'border-border hover:border-primary/50 bg-background'
                                    }`}
                                    onClick={() => handleSelectAddress(address)}
                                  >
                                    <div className="flex items-start gap-2">
                                      <div className={`w-6 h-6 rounded-full bg-secondary flex items-center justify-center ${iconColor}`}>
                                        <AddressIcon className="h-3 w-3" />
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center gap-1 mb-1">
                                          <span className="font-medium">{address.addressType}</span>
                                          {address.isDefault && (
                                            <span className="bg-primary text-white px-1 py-0.5 rounded text-xs">
                                              Default
                                            </span>
                                          )}
                                        </div>
                                        <p className="font-medium">{address.fullName}</p>
                                        <p className="text-muted-foreground line-clamp-1">
                                          {address.addressLine1}, {address.city}
                                        </p>
                                      </div>
                                      {currentAddress?.addressId === address.addressId && (
                                        <div className="w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                                          <Check className="h-2 w-2 text-white" />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <div className="p-3 border rounded-lg bg-amber-50 border-amber-200">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="h-4 w-4 text-amber-600" />
                        <p className="font-medium text-amber-800 text-sm">Add delivery address</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/address-management')}
                        className="w-full text-xs"
                      >
                        Add Address
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal ({totalQty} items)</span>
                  <span>₹{priceSummary.totalSelling}</span>
                </div>

                {priceSummary.totalDiscount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-₹{priceSummary.totalDiscount}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="text-muted-foreground">Delivery</span>
                  <span className={priceSummary.shippingCharge === 0 ? 'text-discount font-medium' : ''}>
                    {priceSummary.shippingCharge === 0 ? 'FREE' : `₹${priceSummary.shippingCharge}`}
                  </span>
                </div>

                {priceSummary.shippingCharge > 0 &&
                  shippingConfig?.freeThreshold > priceSummary.totalSelling && (
                    <p className="text-xs text-muted-foreground">
                      Add ₹{shippingConfig.freeThreshold - priceSummary.totalSelling} more for free delivery
                    </p>
                  )}

              </div>

              <div className="border-t pt-3 mt-3 flex justify-between font-semibold text-base md:text-lg">
                <span>You Pay</span>
                <span className="text-primary">₹{priceSummary.grandTotal}</span>
              </div>

              <Button
                variant="cta"
                size="lg"
                className="w-full mt-6"
                onClick={handleProceedToReview}
              >
                {user.isAuthenticated ? 'Proceed to Review' : 'Sign in to Continue'}
              </Button>

              {/* Trust badges */}
              <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <ShieldCheck className="h-4 w-4" />
                  <span>Secure</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <RotateCcw className="h-4 w-4" />
                  <span>Easy Returns</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CartPage;